/**
 * VP-230: wspólna (server + client) ekstrakcja i naprawa JSON-a ze skryptem
 * stories zwracanym przez LLM. Wcześniej apps/api/modules/stories-creator/service.ts
 * i apps/web/src/components/stories-creator/stories-parser.ts trzymały własne,
 * dryfujące kopie — teraz jedno źródło prawdy.
 *
 * Najczęstsza awaria: model miesza polskie i ASCII cudzysłowy, np.
 *   "body": "Dają „gotowe systemy", które działały 5 lat temu."
 * Niesescape'owany ASCII `"` kończy string w połowie wartości i JSON.parse pada.
 */

const WHITESPACE = new Set([" ", "\t", "\r", "\n"]);

/** Wyciąga blok JSON z odpowiedzi modelu: najpierw ```json fence, potem pierwszy { … ostatni }. */
export function extractJsonBlock(text: string): string | null {
  const fenced = text.match(/```json\s*([\s\S]*?)```/i);
  if (fenced && fenced[1]) return fenced[1].trim();
  const first = text.indexOf("{");
  const last = text.lastIndexOf("}");
  if (first >= 0 && last > first) return text.slice(first, last + 1).trim();
  return null;
}

function skipWhitespace(src: string, from: number): number {
  let k = from;
  while (k < src.length && WHITESPACE.has(src[k])) k++;
  return k;
}

/** Czy od pozycji k zaczyna się wiarygodny kolejny token JSON (po przecinku)? */
function looksLikeNextJsonToken(src: string, k: number): boolean {
  const c = src[k] ?? "";
  if (c === '"' || c === "{" || c === "[" || c === "}" || c === "]" || c === "-") return true;
  if (c >= "0" && c <= "9") return true;
  for (const literal of ["true", "false", "null"]) {
    if (src.startsWith(literal, k)) {
      const after = src[k + literal.length] ?? "";
      if (after === "" || WHITESPACE.has(after) || after === "," || after === "}" || after === "]")
        return true;
    }
  }
  return false;
}

/**
 * Naprawia niesescape'owane ASCII `"` wewnątrz wartości stringów, zamieniając je
 * na U+201D, tak by JSON.parse przeszedł. Cudzysłów uznajemy za zamykający tylko
 * wtedy, gdy to, co po nim następuje, wygląda jak poprawna kontynuacja JSON-a:
 *  - dla klucza: `:`;
 *  - `,` po którym następuje wiarygodny kolejny token (klucz/wartość) — samo
 *    `", słowo` to proza, nie koniec stringa;
 *  - `}` / `]` po którym następuje `,`, `}`, `]` albo koniec tekstu;
 *  - koniec tekstu.
 */
export function repairInnerQuotes(src: string): string {
  let out = "";
  let inString = false;
  let isKeyPosition = false;
  let escape = false;
  let i = 0;

  const lastNonWs = (): string => {
    for (let j = out.length - 1; j >= 0; j--) {
      if (!WHITESPACE.has(out[j])) return out[j];
    }
    return "";
  };

  const closesString = (quoteIndex: number): boolean => {
    const k = skipWhitespace(src, quoteIndex + 1);
    const next = src[k] ?? "";
    if (next === "") return true;
    if (isKeyPosition && next === ":") return true;
    if (next === ",") return looksLikeNextJsonToken(src, skipWhitespace(src, k + 1));
    if (next === "}" || next === "]") {
      const m = skipWhitespace(src, k + 1);
      const after = src[m] ?? "";
      return after === "" || after === "," || after === "}" || after === "]" || after === ":";
    }
    return false;
  };

  while (i < src.length) {
    const c = src[i];
    if (!inString) {
      if (c === '"') {
        const prev = lastNonWs();
        // `{` / `,` poprzedza zarówno klucz, jak i element tablicy stringów —
        // closesString dopuszcza dla tej pozycji i `:`, i separatory tablicowe.
        isKeyPosition = prev === "{" || prev === ",";
        inString = true;
      }
      out += c;
      i++;
      continue;
    }
    if (escape) {
      out += c;
      escape = false;
      i++;
      continue;
    }
    if (c === "\\") {
      out += c;
      escape = true;
      i++;
      continue;
    }
    if (c === '"') {
      if (closesString(i)) {
        out += c;
        inString = false;
        isKeyPosition = false;
      } else {
        out += "”";
      }
      i++;
      continue;
    }
    out += c;
    i++;
  }
  return out;
}
